/** Host OS — Events. The full, filterable list of everything the user runs. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, Repeat, Plane, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import HostLayout from "@/components/host/HostLayout";
import CreateEventPicker from "@/components/CreateEventPicker";
import { EventRow, HostPageHeader } from "@/components/host/hostUi";

const STATUS_TABS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "all", label: "All" },
];
const TYPE_TABS = [
  { id: "all", label: "All", icon: null },
  { id: "game", label: "Sessions", icon: Calendar },
  { id: "series", label: "Series", icon: Repeat },
  { id: "trip", label: "Retreats", icon: Plane },
];
const TYPE_LABEL = { all: "offerings", game: "sessions", series: "series", trip: "retreats" };

const HostEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("upcoming");
  const [typeFilter, setTypeFilter] = useState("all");
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/events?status=${statusFilter}&type=${typeFilter}`, { headers });
      setEvents(res.ok ? await res.json() : []);
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleShare = async (event) => {
    const url = `${window.location.origin}${event.url}`;
    try {
      if (navigator.share) await navigator.share({ title: event.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    } catch (e) { /* cancelled */ }
  };

  return (
    <HostLayout>
      <div data-testid="host-events-page">
        <HostPageHeader
          icon={Calendar}
          title="Offerings"
          subtitle="Every class, session, series and trip you run — filter, manage and share."
          actions={
            <Button onClick={() => setPickerOpen(true)} size="sm" className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="events-new-offering">
              <Plus className="w-4 h-4" /> New offering
            </Button>
          }
        />

        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="inline-flex bg-muted/50 rounded-full p-1" data-testid="host-status-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setStatusFilter(t.id)}
                className={`px-3.5 py-1.5 text-sm font-medium rounded-full transition-colors ${
                  statusFilter === t.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`host-status-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="inline-flex bg-muted/50 rounded-full p-1" data-testid="host-type-tabs">
            {TYPE_TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTypeFilter(t.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors flex items-center gap-1 ${
                    typeFilter === t.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`host-type-${t.id}`}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}{t.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : events.length === 0 ? (
          <Card className="p-8 sm:p-12 border-dashed border-border text-center" data-testid="host-events-empty">
            <p className="text-sm text-muted-foreground">
              No {statusFilter === "all" ? "" : statusFilter + " "}{TYPE_LABEL[typeFilter]} to show.
            </p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Button size="sm" onClick={() => setPickerOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid="host-events-add-offering">
                <Plus className="w-4 h-4" /> Add offering
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {events.map((ev) => (
              <EventRow key={`${ev.type}-${ev.id}`} event={ev} onShare={handleShare} />
            ))}
          </div>
        )}
      </div>
      <CreateEventPicker open={pickerOpen} onOpenChange={setPickerOpen} fromHost />
    </HostLayout>
  );
};

export default HostEventsPage;
